/**
 * MLCOQueueBadge.jsx — Brief 14 PR2
 *
 * Compact shield-icon badge for TopNavBar. Polls
 * GET /api/compliance/mlco-queue/summary every 60s while mounted.
 * Renders a red dot + count when items are waiting (pending_total).
 *
 * Rendered conditionally — TopNavBar only mounts the component when
 * the user has is_mlco || is_colp || is_director (gating fetched once
 * in TopNavBar.jsx). The component itself never throws — fetch
 * failures simply hide the badge.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { API } from "../config/api";

function authHeaders() {
  const email =
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    "";
  return email ? { "X-Bonsai-User-Id": email } : {};
}

const POLL_MS = 60_000;

export default function MLCOQueueBadge() {
  const [count, setCount] = useState(null);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await axios.get(
          `${API}/compliance/mlco-queue/summary`,
          { headers: authHeaders() }
        );
        if (cancelled) return;
        // BM-H631 (26 Sep 2026): the summary's open count is pending_total
        // (pending_fe + pending_mlco). This summed open / fe_responded /
        // mlco_reviewing, states the queue never writes, so it always read 0.
        setCount(Number(res.data?.pending_total) || 0);
      } catch (err) {
        if (cancelled) return;
        setCount(null);
        // BM-H10: on 401 the session is dead — stop the poll so we don't
        // spam the console with a 401 every 60s. The badge hides itself
        // (count === null) and the user will re-authenticate on next
        // navigation. Any other error stays retried (transient network).
        const status = err?.response?.status;
        if (status === 401 && timer.current) {
          clearInterval(timer.current);
          timer.current = null;
        }
      }
    };
    tick();
    timer.current = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      if (timer.current) clearInterval(timer.current);
    };
  }, []);

  const pending = count && count > 0;

  return (
    <button
      type="button"
      onClick={() => navigate("/compliance/mlco-inbox")}
      title={pending ? `${count} MLCO item${count === 1 ? "" : "s"} pending` : "MLCO Inbox"}
      data-testid="topnav-mlco-badge"
      className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
        bg-slate-800 text-slate-300 border border-slate-700/50
        hover:bg-slate-700 hover:text-slate-100
        transition-colors duration-150 whitespace-nowrap`}
    >
      <ShieldAlert size={14} className="shrink-0" />
      <span className="hidden sm:inline">MLCO</span>
      {pending && (
        <span
          data-testid="topnav-mlco-badge-count"
          className="ml-1 min-w-[1rem] h-4 px-1 inline-flex items-center justify-center rounded-full bg-red-700 text-white text-[10px] font-semibold leading-none"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}
